/**
 * activationLandingViewModel — `(reduxState, raw) → props de <ActivationLandingPage>`.
 *
 * Données en patron B : la réponse de `getActivationLinkContext` n'atteint jamais `state.data`
 * (c'est un `JsonResponse` nu, pas une Resource), elle est tenue par `useActivationLandingData`.
 * Seule l'erreur de la tranche api est lue ici — c'est elle qui distingue un lien illisible d'une
 * panne passagère (`ActivationLandingScreen.js:159-170`).
 *
 * L'ordre des gardes est celui du legacy, et il compte : lien absent ou erreur d'abord, attente
 * ensuite, état de page en dernier.
 */
import { createSelector } from "reselect"

import {
    ACTIVATION_EXPIRED_LINK_OUTCOME,
    ACTIVATION_UNREADABLE_OFFER_OUTCOME,
    toUIActivationLanding,
    type ApiActivationLinkContext,
} from "../../../adapters/parent/Activation/activationAdapter"
import {
    type UIActivationLandingState,
    type UIActivationNotice,
    type UIActivationOutcome,
    type UIActivationProduct,
    type UISubscriptionNextStep,
} from "../../../ui/types"
import { selectAccount } from "../../shared/displayedStudent"

export type ActivationLandingViewModel = {
    studentFirstName: string
    product: UIActivationProduct | null
    nextStep: UISubscriptionNextStep | null
    notice: UIActivationNotice | null
    outcome: UIActivationOutcome | null
    loading: boolean
    /** L'état tranché, pour l'événement Amplitude et le geste de reprise. */
    landingState: UIActivationLandingState | null
    repriseSeason: string | null
}

const EMPTY: Omit<ActivationLandingViewModel, "outcome" | "loading"> = {
    studentFirstName: "votre enfant",
    product: null,
    nextStep: null,
    notice: null,
    landingState: null,
    repriseSeason: null,
}

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type ReduxState = any

export const activationLandingViewModel = createSelector(
    [
        (state: ReduxState) => state?.api?.getActivationLinkContext?.error as { status?: number } | undefined,
        (_state: ReduxState, raw: ApiActivationLinkContext | null) => raw,
        (_state: ReduxState, _raw: ApiActivationLinkContext | null, hasLinkParams: boolean) => hasLinkParams,
        // Les horloges du composeur qu'ouvre le bouton de la carte (`ActivationLandingScreen.js`,
        // `mapStateToProps`) : la carte et la grille disent la même heure. Vides pour un visiteur
        // anonyme — les deux affichent alors les heures de Paris, ensemble.
        (state: ReduxState) => selectAccount(state)?.timezone as string | undefined,
        (state: ReduxState) => state?.data?.staticData?.server_timezone as string | undefined,
    ],
    (error, raw, hasLinkParams, userTimezone, serverTimezone): ActivationLandingViewModel => {
        // Un lien illisible et une panne passagère méritent deux messages différents : dire « ce lien
        // n'est plus valide » sur un 500 enverrait le parent chercher un problème qui n'existe pas.
        if (!hasLinkParams || error) {
            const unreadableLink = !hasLinkParams || error?.status === 403

            return {
                ...EMPTY,
                outcome: unreadableLink ? ACTIVATION_EXPIRED_LINK_OUTCOME : ACTIVATION_UNREADABLE_OFFER_OUTCOME,
                loading: false,
            }
        }

        if (raw == null) return { ...EMPTY, outcome: null, loading: true }

        const landing = toUIActivationLanding(raw, undefined, { userTimezone, serverTimezone })

        return {
            studentFirstName: landing.studentFirstName,
            product: landing.product,
            nextStep: landing.nextStep,
            notice: landing.notice,
            outcome: landing.outcome,
            loading: false,
            landingState: landing.state,
            repriseSeason: landing.repriseSeason,
        }
    }
)
